import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../../api.ts';
import { fmtDay, type BillingState } from './types.ts';

const ERRORS: Record<string, string> = {
  MISSING_FIELDS: 'L’adresse, le code postal et la ville sont obligatoires.',
  INVALID_SIRET: 'Le SIRET doit comporter 14 chiffres.',
  INVALID_VAT_REGIME: 'Indiquez votre régime de TVA.',
  INVALID_VAT_NUMBER: 'Numéro de TVA intracommunautaire invalide (ex. FR12345678901).',
  INVALID_TRADE_NAME: 'Le nom commercial est invalide.',
};

export function BillingProfile() {
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [form, setForm] = useState<BillingState['profile'] | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(() => {
    api.get<BillingState>('/extra/billing').then((b) => {
      setBilling(b);
      setForm(b.profile);
      setEditing(b.missing.length > 0);
    });
  }, []);
  useEffect(load, [load]);

  if (!billing || !form) return <div className="empty-state">Chargement…</div>;
  const set = (patch: Partial<BillingState['profile']>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const accepted = Boolean(billing.mandate.acceptedAt);

  function issuedNote(count: number) {
    return count > 0 ? ` ${count} facture(s) de mission en attente ont été émises.` : '';
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.put<BillingState & { issuedInvoices: number }>('/extra/billing', form);
      setBilling(res);
      setForm(res.profile);
      setEditing(false);
      setMessage({ kind: 'ok', text: `Informations enregistrées.${issuedNote(res.issuedInvoices)}` });
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.' });
    } finally {
      setBusy(false);
    }
  }

  async function toggleMandate() {
    setMessage(null);
    if (accepted) {
      if (!window.confirm('Révoquer le mandat ? Mon Extra n’établira plus vos factures de mission : vous devrez les établir vous-même.')) return;
      setBusy(true);
      try {
        await api.delete('/extra/mandate');
        load();
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ issuedInvoices: number }>('/extra/mandate/accept');
      setMessage({ kind: 'ok', text: `Mandat accepté.${issuedNote(res.issuedInvoices)}` });
      load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {message && <div className={message.kind === 'ok' ? 'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px' }}>{message.text}</div>}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800 }}>Mes informations de facturation</div>
          {!editing && <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Modifier</button>}
        </div>
        {editing ? (
          <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Facturé au nom de <strong>{form.legalName}</strong> (entrepreneur individuel).</div>
            <input className="input" placeholder="Nom commercial (facultatif)" value={form.tradeName} onChange={(e) => set({ tradeName: e.target.value })} />
            <input className="input" required placeholder="SIRET (14 chiffres)" value={form.siret} onChange={(e) => set({ siret: e.target.value })} />
            <input className="input" required placeholder="Adresse" value={form.address} onChange={(e) => set({ address: e.target.value })} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <input className="input" required placeholder="Code postal" value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} />
              <input className="input" required placeholder="Ville" value={form.city} onChange={(e) => set({ city: e.target.value })} />
            </div>
            <select className="input" required value={form.vatRegime ?? ''} onChange={(e) => set({ vatRegime: (e.target.value || null) as BillingState['profile']['vatRegime'] })}>
              <option value="">Régime de TVA…</option>
              <option value="FRANCHISE">Franchise en base de TVA (cas courant du micro-entrepreneur)</option>
              <option value="LIABLE">Assujetti à la TVA</option>
            </select>
            {form.vatRegime === 'LIABLE' && (
              <input className="input" required placeholder="N° de TVA intracommunautaire (FR…)" value={form.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })} />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy}>Enregistrer</button>
              {billing.missing.length === 0 && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setForm(billing.profile); setEditing(false); }}>Annuler</button>}
            </div>
          </form>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--text)' }}>{billing.profile.legalName}</strong>{billing.profile.tradeName ? ` · ${billing.profile.tradeName}` : ''}<br />
            SIRET {billing.profile.siret}<br />
            {billing.profile.address}, {billing.profile.postalCode} {billing.profile.city}<br />
            {billing.profile.vatRegime === 'FRANCHISE' ? 'Franchise en base de TVA' : `Assujetti à la TVA · ${billing.profile.vatNumber}`}
          </div>
        )}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Mandat de facturation</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{billing.mandate.text}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${accepted ? 'btn-secondary' : 'btn-primary'}`} onClick={toggleMandate} disabled={busy}>
            {accepted ? 'Révoquer le mandat' : 'J’accepte le mandat'}
          </button>
          {billing.mandate.acceptedAt && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Accepté le {fmtDay(billing.mandate.acceptedAt)}</span>}
        </div>
      </div>
    </>
  );
}
